import React, { Fragment, useCallback, useEffect, useState } from "react";
import { Container } from "@/components/container";
import { JobContent } from "./";
import { Navbar, NavbarActions } from "@/partials/navbar";
import { KeenIcon } from "@/components";
import { PageMenu } from "./menu/PageMenu";
import { useParams } from "react-router-dom";
import axios from "axios";
import { enqueueSnackbar } from "notistack";

import { JobDetailsData } from "@/pages/buddy/types/JobDetailsData";
import { Loading } from "@/layouts/buddy/loading";
import { JobModal } from "@/pages/buddy/job/blocks/source/JobModal";
import { CandidateModal } from "@/pages/buddy/candidate/blocks/source/CandidateModal";
import { JobProfileHero } from "@/pages/buddy/job/hero/JobPofileHero";

const JobPage = () => {
    const [job, setJob] = useState<JobDetailsData | null>(null);
    const [loading, setLoading] = useState(true);
    const { tab, p_id } = useParams<{ tab?: string; p_id: string }>();
    const [activePage, setActivePage] = useState<string>(tab || "overview");
    const [noActiveCandidates, setNoActiveCandidates] = useState<boolean>(false);
    const [refreshKey, setRefreshKey] = useState(0); // Forces data refresh

    const [jobModalOpen, setJobModalOpen] = useState(false);
    const handleJobModalOpen = () => setJobModalOpen(true);
    const handleJobModalClose = () => {
        setJobModalOpen(false);
        triggerRefresh();
    };

    const [candidateModalOpen, setCandidateModalOpen] = useState(false);
    const handleCandidateModalOpen = () => setCandidateModalOpen(true);
    const handleCandidateModalClose = () => {
        setCandidateModalOpen(false);
        triggerRefresh();
    };

    const API_JOB = `${import.meta.env.VITE_APP_API_URL}/job`;

    const triggerRefresh = () => setRefreshKey(prev => prev + 1);

    const fetchJobData = async () => {
        setLoading(true);
        try {
            const response = await axios.get<JobDetailsData>(`${API_JOB}/${p_id}`, {
                headers: { Accept: "application/json" },
            });
            setJob(response.data);
        } catch (err) {
            enqueueSnackbar(`Failed to fetch job data: ${err}`, {
                variant: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchJobData();
    }, [p_id, refreshKey]);

    const handleSummaryFetched = useCallback((activeCandidates: number) => {
        setNoActiveCandidates(activeCandidates === 0);
    }, []);

    if (loading) return <Loading loading={loading} />;
    if (!job) return <div className="text-center mt-10 text-error">No job data available.</div>;

    return (
        <Fragment>
            <JobProfileHero job={job} />

            <JobModal
                mode="addSource"
                open={jobModalOpen}
                onClose={handleJobModalClose}
                job={job}
                refreshJobData={handleJobModalClose}
            />

            <CandidateModal
                mode="create"
                open={candidateModalOpen}
                onClose={handleCandidateModalClose}
                preselectedJob={job}
                refreshCandidateData={handleCandidateModalClose}
            />

            <Container>
                <Navbar>
                    <PageMenu setActivePage={setActivePage} />
                    <NavbarActions>
                        <button
                            onClick={handleJobModalOpen}
                            type="button"
                            className="btn btn-sm btn-primary ml-2"
                        >
                            <KeenIcon icon="plus" /> Add New Source
                        </button>
                        <button
                            onClick={handleCandidateModalOpen}
                            type="button"
                            className={`btn btn-sm btn-primary ml-2 ${noActiveCandidates ? "pulsating" : ""}`}
                        >
                            <KeenIcon icon="plus" /> Add New Candidate
                        </button>
                        {/* <NavbarDropdown /> */}
                    </NavbarActions>
                </Navbar>
            </Container>

            <Container>
                <JobContent
                    job={job}
                    activePage={activePage}
                    loading={loading}
                    setActivePage={setActivePage}
                    handleSummaryFetched={handleSummaryFetched}
                    onCpsUpdateComplete={triggerRefresh}
                />
            </Container>
        </Fragment>
    );
};

export { JobPage };
